import { CaretUpDown, GlobeHemisphereWest } from "@phosphor-icons/react"
import { trackEvent } from "~/hooks/useTrackingService"
import { useSkuStore } from "~/providers/sku-store-provider"
import { cn } from "~/utils/helpers"
import { useForecastingChannels } from "~/hooks/useChannels"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { Button } from "~/components/design-system/components"
import { DisabledTooltip } from "~/components/reusable/DisabledTooltip"
import { useState } from "react"
import { channelNameOf } from "~/utils/demandScope"

export default function GlobalChannelSelect({
  className,
  value,
  onChange,
  disabledReason,
}: {
  className?: string
  value?: string | undefined
  onChange?: (channel: string | undefined) => void
  disabledReason?: string
}) {
  const storeChannel = useSkuStore((state) => state.channel)
  const updateChannel = useSkuStore((state) => state.updateChannel)
  const controlled = onChange !== undefined
  const selectedChannel = controlled ? value : storeChannel

  const [open, setOpen] = useState(false)

  const setChannel = (id: string | undefined, name: string) => {
    if (controlled) onChange(id)
    else {
      updateChannel(id)
      trackEvent("Channel Selected", { Channel: name })
    }
    setOpen(false)
  }

  const { data: channels } = useForecastingChannels()
  const showChannels = (channels?.length ?? 0) > 1
  const selectedName = selectedChannel && channelNameOf(channels, selectedChannel)

  const trigger = (
    <Button size="sm" variant="neutral" disabled={disabledReason != null}>
      <GlobeHemisphereWest />
      {selectedName ?? "All channels"}
      <CaretUpDown />
    </Button>
  )

  if (showChannels && disabledReason)
    return <DisabledTooltip title={disabledReason}>{trigger}</DisabledTooltip>

  return showChannels ? (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className={cn(
          "min-w-[160px] max-w-[160px] rounded-[12px] p-0 overflow-hidden divide-y divide-border-base",
          className,
        )}
        align="start"
        sideOffset={8}
      >
        <div className="flex flex-col max-h-[200px] overflow-y-auto *:transition-colors">
          <div
            className={cn(
              "px-3 py-2 text-sm cursor-pointer hover:bg-background-highlight-hover",
              !selectedChannel && "bg-background-highlight",
            )}
            onClick={() => setChannel(undefined, "All")}
          >
            All Channels
          </div>
          {channels?.map((channel) => (
            <div
              key={channel.id}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer hover:bg-background-highlight-hover flex items-center gap-2",
                selectedChannel === channel.id && "bg-background-highlight",
              )}
              onClick={() => setChannel(channel.id, channel.name)}
            >
              {channel.name}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  ) : null
}
